/*
 * Copyright (c) 2023. Collabro Limited
 */

import React, { useState } from "react";
import {
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import { Credentials, UserDetails } from "../../interface/credentials";
import { useQuery } from "@tanstack/react-query";
import { authenticate } from "../../api";
import { AxiosError } from "axios";
import { Theme } from "@mui/material/styles";

interface LoginComponent {
  setLoggedInUser: any;
  setLogout: any;
}

const initialUser: UserDetails = {
  username: "",
  password: "",
};

const Login = (props: LoginComponent) => {
  const theme: Theme = useTheme();
  const [user, setUser] = useState<UserDetails>(initialUser);

  const { isInitialLoading, isError, error, refetch, isFetching } = useQuery(
    ["user", user],
    () => authenticate(user),
    {
      enabled: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: (data: Credentials) => {
        props.setLoggedInUser(data.principal);
        props.setLogout(false);
      },
    }
  );

  const handleSubmit = async (e: any): Promise<void> => {
    e.preventDefault();
    await refetch();
  };

  return (
    <Grid container>
      <form onSubmit={handleSubmit}>
        <Paper
          item
          xs={12}
          md={6}
          container
          spacing={1}
          elevation={5}
          component={Grid}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            width: 400,
            background: { xs: "none", md: theme.palette.modal.main },
            display: "flex",
            flexDirection: "column",
            alignContent: "center",
            alignItems: "center",
            padding: 2,
            borderRadius: 10,
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              padding: "0 !important",
              minHeight: 135.5,
            }}
          >
            <img src={theme.logo.small} alt="Company Logo" width="150rem" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h4">Roster Panel</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2">
              Sign In with your Vantage Account
            </Typography>
          </Grid>
          {isError && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ width: 250 }}>
                {(error as AxiosError).response!.data as string}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              variant="filled"
              label="Username"
              value={user.username}
              type="text"
              onChange={(e: any) =>
                setUser({ ...user, username: e.target.value })
              }
              fullWidth
              required
              sx={{
                width: 250,
              }}
              InputProps={{
                sx: {
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} mt={-1}>
            <TextField
              variant="filled"
              label="Password"
              autoComplete="off"
              type="password"
              onChange={(e: any) =>
                setUser({ ...user, password: e.target.value })
              }
              value={user.password}
              sx={{
                width: 250,
              }}
              required
              InputProps={{
                autoComplete: "off",
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton color="primary" type="submit" size="small">
                      {isFetching || isInitialLoading ? (
                        <CircularProgress size="1.5rem" />
                      ) : (
                        <ArrowCircleRightIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  borderBottomLeftRadius: 10,
                  borderBottomRightRadius: 10,
                  "&:before": {
                    content: "none",
                  },
                  "&.Mui-focused": {
                    "&:after": {
                      content: '"\\00a0"',
                      borderRadius: 10,
                    },
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" ml={1}>
              Copyright (c) 2023. Collabro Limited
            </Typography>
          </Grid>
        </Paper>
      </form>
    </Grid>
  );
};

export default Login;
